import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated walkthrough of unlocking a locked session: the lock card with the
 * signed-in user's identity, PIN dots filling as digits are entered, Unlock,
 * done. Mirrors PinLockOverlay.jsx (identity row, 6 dots, keypad, the
 * "Forgot your PIN?" and "Not me — sign out" links) — but rendered inline in
 * the stage, never position:fixed, so the mock can't escape the demo frame.
 */

const STEPS = [
  {
    caption: 'When the session locks, the app is hidden behind this card — your name stays visible so the wrong person knows to sign out.',
    scene: 'locked', ms: 2800,
    cursor: 'key',
  },
  {
    caption: 'Enter your 6-digit PIN with the keypad or your keyboard…',
    scene: 'pin2', ms: 1400,
    cursor: 'key', click: true,
  },
  {
    caption: '…each digit fills a dot.',
    scene: 'pin4', ms: 1400,
    cursor: 'key', click: true,
  },
  {
    caption: 'All six digits in — Unlock.',
    scene: 'pin6', ms: 1600,
    cursor: 'unlock', click: true,
  },
  {
    caption: 'Unlocked — every other tab you have open unlocks with you.',
    scene: 'unlocked', ms: 2800,
  },
];

const FILLED = { locked: 0, pin2: 2, pin4: 4, pin6: 6, unlocked: 6 };

function PinDots({ filled }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '12px 0 2px' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 12, height: 12, borderRadius: '50%',
            background: i < filled ? 'var(--brand)' : 'transparent',
            border: '1.5px solid ' + (i < filled ? 'var(--brand)' : 'var(--border-strong)'),
            transition: 'background 120ms, border-color 120ms',
          }}
        />
      ))}
    </div>
  );
}

export default function PinLockDemo() {
  return (
    <DemoStage steps={STEPS} label="Unlocking a locked session" height={290}>
      {(step) => {
        const scene = step?.scene ?? 'locked';
        const unlocked = scene === 'unlocked';
        return (
          <div className="kb-mock" style={{ maxWidth: 300 }}>
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 14, padding: 16,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
            }}>
              {/* Identity row — who this lock belongs to */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span className="kb-mock-avatar">JD</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>J. Doyle</div>
                  <span className="kb-skel" style={{ width: 110, height: 9, marginTop: 3 }} />
                </div>
              </div>

              {unlocked ? (
                <div className="kb-mock-success kb-pop" style={{ padding: '10px 0' }}>
                  <span className="kb-mock-tick">✓</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>Unlocked</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                      Back to where you left off.
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text)' }}>Session locked</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
                    Enter your PIN to continue.
                  </div>

                  <PinDots filled={FILLED[scene] ?? 0} />

                  {/* One keypad row — enough to signal the full 0-9 pad */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
                    {['1', '2', '3'].map((k) => (
                      <button
                        key={k}
                        type="button"
                        tabIndex={-1}
                        data-kb-target={k === '2' ? 'key' : undefined}
                        style={{
                          padding: '8px 0', fontSize: 15, fontWeight: 500,
                          background: 'var(--surface)',
                          border: '1px solid var(--border-strong)',
                          borderRadius: 10, color: 'var(--text)', cursor: 'pointer',
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    tabIndex={-1}
                    className="btn primary"
                    data-kb-target="unlock"
                    style={{
                      width: '100%', marginTop: 10, justifyContent: 'center', gap: 0,
                      opacity: scene === 'pin6' ? 1 : 0.55,
                      transition: 'opacity 200ms var(--ease)',
                    }}
                  >
                    Unlock
                  </button>

                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    marginTop: 8, fontSize: 11, color: 'var(--text-3)',
                  }}>
                    <span style={{ color: 'var(--brand-ink)' }}>Forgot your PIN?</span>
                    <span>Not me — sign out</span>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
